import { useCallback, useEffect, useRef } from 'react';
import { apiPost } from '../../lib/api';
import { flashToast } from './helpers';

/**
 * The four proposal-card lifecycle handlers (confirm / delete / edit-open /
 * edit-cancel / edit-save), plus the inflight de-dupe set. Pulled out of
 * Reflection.js to keep that file under 500 lines.
 *
 * The hook is stateless about the thread itself — it just receives `setThread`
 * and a `threadRef` (for synchronous reads) so all proposal-state ownership
 * stays inside Reflection.
 *
 * Args:
 *   { haptics, nextId, threadRef, setThread, setEditingId, queueFailedApply }
 *
 * Returns:
 *   { onConfirm, onDelete, onEditOpen, onEditCancel, onEditSave }
 */
export function useProposalHandlers({
  haptics,
  nextId,
  threadRef,
  setThread,
  setEditingId,
  queueFailedApply,
}) {
  const inFlightApplies = useRef(new Set()); // double-tap guard
  const transientTimers = useRef(new Set()); // pending setTimeout ids for transient notes

  // Clear all pending transient-note timers on unmount to avoid setState-after-unmount.
  useEffect(() => () => {
    transientTimers.current.forEach((id) => clearTimeout(id));
    transientTimers.current.clear();
  }, []);

  // Append a transient "Set." style note, then auto-clear it ~2s later so the
  // diary doesn't accumulate acknowledgements. Each note has its own timer.
  const appendTransientNote = useCallback((text) => {
    const id = nextId();
    setThread((t) => t.concat({ id, kind: 'note', text }));
    const timerId = setTimeout(() => {
      transientTimers.current.delete(timerId);
      setThread((t) => t.filter((x) => x.id !== id));
    }, 2000);
    transientTimers.current.add(timerId);
  }, [nextId, setThread]);

  const onConfirm = useCallback(async (changeId) => {
    if (inFlightApplies.current.has(changeId)) return;
    inFlightApplies.current.add(changeId);
    haptics.trigger('medium');

    // Capture the original card BEFORE optimistic removal, so we can restore on hard failure
    const original = threadRef.current.find(
      (x) => x.kind === 'proposal' && x.change.change_id === changeId
    );

    // Optimistically remove the proposal from the thread BEFORE the network call
    setThread((t) => t.filter((x) => !(x.kind === 'proposal' && x.change.change_id === changeId)));

    try {
      let r = null;
      let networkErr = false;
      try {
        r = await apiPost(`/api/pending-changes/${changeId}/apply`, {});
      } catch (_) { networkErr = true; }

      if (r && (r.ok || r.status === 404 || r.status === 409)) {
        // 404/409 = already applied or rejected by another tab — silently ok
        const text = (r.status === 404 || r.status === 409) ? 'Already handled.' : 'Set.';
        appendTransientNote(text);
        haptics.trigger('success');
      } else if (networkErr && !navigator.onLine) {
        // queue for retry, optimistically resolve with a sync note
        queueFailedApply(changeId);
        appendTransientNote("Saved — I'll sync when you're online.");
      } else {
        flashToast("Couldn't apply — try again.", 'error');
        // Re-add proposal so user can retry — preserves the original change_data
        if (original) {
          setThread((t) => t.concat({ id: nextId(), kind: 'proposal', change: original.change }));
        }
      }
    } finally {
      inFlightApplies.current.delete(changeId);
    }
  }, [haptics, queueFailedApply, setThread, threadRef, appendTransientNote]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDelete = useCallback(async (changeId) => {
    if (inFlightApplies.current.has(changeId)) return;
    inFlightApplies.current.add(changeId);
    haptics.trigger('light');
    try {
      try {
        await apiPost(`/api/pending-changes/${changeId}/reject`, {});
        // 404 silently ok — already handled elsewhere
      } catch (_) { /* fire-and-forget for rejects */ }
      setThread((t) => t.filter((x) => !(x.kind === 'proposal' && x.change.change_id === changeId)));
      appendTransientNote('Got it — dropped.');
    } finally {
      inFlightApplies.current.delete(changeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [haptics, setThread, appendTransientNote]);

  const onEditOpen = useCallback((changeId) => {
    haptics.trigger('light');
    setEditingId(changeId);
  }, [haptics, setEditingId]);

  const onEditCancel = useCallback(() => {
    setEditingId(null);
  }, [setEditingId]);

  const onEditSave = useCallback(async (changeId, fields) => {
    if (inFlightApplies.current.has(changeId)) return;
    inFlightApplies.current.add(changeId);
    haptics.trigger('medium');
    try {
      // Find the original proposal so we can merge change_data — read synchronously via ref
      const found = threadRef.current.find(
        (x) => x.kind === 'proposal' && x.change.change_id === changeId
      );
      const original = found ? found.change : null;
      if (!original) {
        flashToast("Couldn't save — try again.", 'error');
        return;
      }

      const merged_change_data = {
        ...(original.change_data || {}),
        name: fields.name,
        duration_minutes: fields.duration_minutes,
        for_today: fields.for_today,
      };

      let r = null;
      try {
        r = await apiPost(`/api/pending-changes/${changeId}/edit`, { change_data: merged_change_data });
      } catch (_) { r = null; }

      if (r && (r.ok || r.status === 404 || r.status === 409)) {
        const detail = (r.status === 404 || r.status === 409)
          ? 'Already handled.'
          : `Saved — ${fields.name}, ${fields.duration_minutes} min.`;
        setThread((t) => t.filter((x) => !(x.kind === 'proposal' && x.change.change_id === changeId)));
        appendTransientNote(detail);
        setEditingId(null);
        haptics.trigger('success');
      } else {
        flashToast("Couldn't save — try again.", 'error');
      }
    } finally {
      inFlightApplies.current.delete(changeId);
    }
  }, [haptics, nextId, setThread, setEditingId, threadRef, appendTransientNote]); // eslint-disable-line react-hooks/exhaustive-deps

  return { onConfirm, onDelete, onEditOpen, onEditCancel, onEditSave };
}

export default useProposalHandlers;
